/**
 * One shell, state-toggled screens — the design is a single app frame, not 14 pages.
 * Routes carry their header title and back affordance here so AppHeader reads them
 * from one place instead of each screen setting chrome for itself.
 */
export const paths = {
  login: '/login',
  home: '/',
  pallets: '/pallets',
  pallet: '/pallets/:palletCode',
  manual: '/manual',
  basket: '/basket',
  assign: '/assign',
  assignResult: '/assign/result',
  queue: '/queue',
  settings: '/settings',
} as const

interface ChromeSpec {
  title: string
  canGoBack: boolean
  /** Login hides the header and the network strip entirely. */
  showChrome: boolean
}

/**
 * Titles are the artboard's TITLES map, verbatim. They are NOT the labels in the
 * prototype's screen-picker harness — those read «شناسایی پالت» and «جزئیات پالت», which
 * are names for the reader of the spec sheet, not header copy for the operator.
 */
const CHROME: Array<{
  match: RegExp
  spec: ChromeSpec
  /** The pallet screen's title is the pallet code itself. */
  titleFrom?: (match: RegExpExecArray) => string
}> = [
  { match: /^\/login$/, spec: { title: 'ورود', canGoBack: false, showChrome: false } },
  { match: /^\/$/, spec: { title: 'خانه', canGoBack: false, showChrome: true } },
  { match: /^\/pallets$/, spec: { title: 'پالت‌ها', canGoBack: true, showChrome: true } },
  {
    match: /^\/pallets\/([^/]+)$/,
    spec: { title: 'پالت', canGoBack: true, showChrome: true },
    titleFrom: (match) => safeDecode(match[1]),
  },
  { match: /^\/manual$/, spec: { title: 'ورود دستی کد', canGoBack: true, showChrome: true } },
  { match: /^\/basket$/, spec: { title: 'سبد موقت', canGoBack: true, showChrome: true } },
  { match: /^\/assign$/, spec: { title: 'تخصیص به پالت', canGoBack: true, showChrome: true } },
  { match: /^\/assign\/result$/, spec: { title: 'نتیجه تخصیص', canGoBack: true, showChrome: true } },
  { match: /^\/queue$/, spec: { title: 'صف ارسال', canGoBack: true, showChrome: true } },
  { match: /^\/settings$/, spec: { title: 'تنظیمات', canGoBack: true, showChrome: true } },
]

const FALLBACK: ChromeSpec = { title: 'خانه', canGoBack: true, showChrome: true }

export function chromeFor(pathname: string): ChromeSpec {
  for (const route of CHROME) {
    const match = route.match.exec(pathname)

    if (match === null) {
      continue
    }

    const derived = route.titleFrom?.(match)

    // The artboard falls back to «پالت» when it has no code; so does this.
    return derived !== undefined && derived !== '' ? { ...route.spec, title: derived } : route.spec
  }

  return FALLBACK
}

export function palletPath(palletCode: string): string {
  return `/pallets/${encodeURIComponent(palletCode)}`
}

function safeDecode(value: string | undefined): string {
  if (value === undefined) {
    return ''
  }

  try {
    return decodeURIComponent(value)
  } catch {
    // A malformed escape must not blank the header.
    return value
  }
}
